import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  ptCreateTask,
  ptDeleteTask,
  ptListTasks,
  ptToggleTask,
  ptUpdateTask,
  ptUpdateTaskStatus,
} from "../../../services/personalTasks";
import {
  PRIORITIES,
  STATUSES,
  dayKey,
  fmtDateTime,
  fmtDayLabel,
  priorityBadge,
  statusBadge,
  taskDate,
} from "./taskHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// My Tasks (Reports → My Tasks) — a personal daily-routine task list for the
// super-admin. Own service (/api/personal-tasks), no shared state with any
// other module. Backend enforces super-admin only; this page also refuses to
// render for anyone else in case the link is reached directly.
// ─────────────────────────────────────────────────────────────────────────────

// Light row-highlight per stage — explicit hex so it doesn't depend on the
// theme's Bootstrap CSS variables resolving a particular way.
const statusRowStyle = (status) => {
  if (status === "Complete") return { backgroundColor: "#e6f7ec" }; // light green
  if (status === "In Progress") return { backgroundColor: "#e7f3fd" }; // light blue
  return { backgroundColor: "#fdeaea" }; // Pending — light pink/red
};

const fmtDue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const now = new Date();
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  const dateLabel = sameDay(d, now)
    ? "Today"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  const timeLabel = hasTime
    ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
    : "";
  return [dateLabel, timeLabel].filter(Boolean).join(" · ");
};

const toDateTimeInputValue = (date, time) => {
  if (!date) return null;
  return `${date}T${time || "00:00"}`;
};

const emptyDraft = () => ({
  title: "",
  description: "",
  dueDate: "",
  dueTime: "",
  priority: "Medium",
  recurring: "None",
  remarks: "",
});

const MyTasks = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [view, setView] = useState("all"); // all | today | overdue | pending | completed
  // Status-wise filter — "" = every status, otherwise one of STATUSES
  // ("Pending" | "In Progress" | "Complete"). Independent of `view` above
  // (the date-based tabs); both are sent together and combine on the server.
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft());
  // Per-task draft for the stage (status + remark) update row, keyed by task id.
  const [statusDrafts, setStatusDrafts] = useState({});
  const [openHistoryId, setOpenHistoryId] = useState(null);

  const getStatusDraft = (task) =>
    statusDrafts[task._id] || { status: task.status || "Pending", remarks: "" };
  const setStatusDraftField = (task, key, value) =>
    setStatusDrafts((prev) => ({
      ...prev,
      [task._id]: { ...getStatusDraft(task), [key]: value },
    }));

  const load = (nextView = view, nextStatus = statusFilter) => {
    setLoading(true);
    ptListTasks({ view: nextView, status: nextStatus || undefined })
      .then((r) => {
        setTasks(r.data?.tasks || []);
        setCounts(r.data?.counts || {});
      })
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperAdmin) load(view, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, statusFilter, isSuperAdmin]);

  const tabs = useMemo(
    () => [
      { key: "all", label: "All", count: counts.total },
      { key: "today", label: "Today", count: counts.today },
      { key: "overdue", label: "Overdue", count: counts.overdue },
      { key: "completed", label: "Completed", count: counts.completed },
    ],
    [counts],
  );

  const statusCount = (s) =>
    s === "Pending" ? counts.pending : s === "In Progress" ? counts.inProgress : counts.completed;

  const handleAdd = async () => {
    if (!draft.title.trim()) {
      Swal.fire({ icon: "warning", title: "Task title is required." });
      return;
    }
    setSaving(true);
    try {
      await ptCreateTask({
        title: draft.title.trim(),
        description: draft.description.trim(),
        dueAt: toDateTimeInputValue(draft.dueDate, draft.dueTime),
        priority: draft.priority,
        recurring: draft.recurring,
        remarks: draft.remarks.trim(),
      });
      setDraft(emptyDraft());
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not add task",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (task) => {
    // Optimistic flip so the checkbox feels instant.
    const nextCompleted = !task.completed;
    setTasks((prev) =>
      prev.map((t) =>
        t._id === task._id
          ? {
              ...t,
              completed: nextCompleted,
              status: nextCompleted ? "Complete" : "Pending",
            }
          : t,
      ),
    );
    try {
      await ptToggleTask(task._id);
      load();
    } catch (err) {
      load();
    }
  };

  const handleUpdateStatus = async (task) => {
    const draft = getStatusDraft(task);
    // Nothing to record if the stage is unchanged and no remark was left —
    // this is what was flooding a task's history with repeated identical
    // "Pending" rows every time Update got clicked without changing anything.
    if (draft.status === task.status && !draft.remarks.trim()) {
      Swal.fire({
        icon: "info",
        title: "Nothing to update",
        text: "Change the status or add a remark before updating.",
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }
    setSaving(true);
    try {
      await ptUpdateTaskStatus(task._id, {
        status: draft.status,
        remarks: draft.remarks,
      });
      setStatusDrafts((prev) => ({
        ...prev,
        [task._id]: { status: draft.status, remarks: "" },
      }));
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not update status",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (task) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete this task?",
      text: task.title,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      await ptDeleteTask(task._id);
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not delete task" });
    }
  };

  const startEdit = (task) => {
    const d = task.dueAt ? new Date(task.dueAt) : null;
    setEditingId(task._id);
    setEditDraft({
      title: task.title,
      description: task.description || "",
      dueDate: d ? d.toISOString().slice(0, 10) : "",
      dueTime:
        d && (d.getHours() !== 0 || d.getMinutes() !== 0)
          ? d.toTimeString().slice(0, 5)
          : "",
      priority: task.priority,
      recurring: task.recurring,
    });
  };

  const saveEdit = async () => {
    if (!editDraft.title.trim()) return;
    setSaving(true);
    try {
      await ptUpdateTask(editingId, {
        title: editDraft.title.trim(),
        description: editDraft.description.trim(),
        dueAt: toDateTimeInputValue(editDraft.dueDate, editDraft.dueTime),
        priority: editDraft.priority,
        recurring: editDraft.recurring,
      });
      setEditingId(null);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not update task",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <>
        <PageTitle activeMenu="My Tasks" motherMenu="Reports" />
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            This page is available to super-admin only.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="My Tasks" motherMenu="Reports" />

      <div className="card mb-3">
        <div className="card-header">
          <h4 className="card-title mb-0">Add a task</h4>
        </div>
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-sm-4">
              <label className="form-label small mb-1">Title</label>
              <input
                className="form-control form-control-sm"
                value={draft.title}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, title: e.target.value }))
                }
                placeholder="e.g. Review daybook balance"
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
            <div className="col-sm-2">
              <label className="form-label small mb-1">Due date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draft.dueDate}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, dueDate: e.target.value }))
                }
              />
            </div>
            <div className="col-sm-2">
              <label className="form-label small mb-1">Time</label>
              <input
                type="time"
                className="form-control form-control-sm"
                value={draft.dueTime}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, dueTime: e.target.value }))
                }
              />
            </div>
            <div className="col-sm-2">
              <label className="form-label small mb-1">Priority</label>
              <select
                className="form-select form-select-sm"
                value={draft.priority}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, priority: e.target.value }))
                }
              >
                {PRIORITIES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="col-sm-1">
              <div className="form-check mt-4">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="ptDaily"
                  checked={draft.recurring === "Daily"}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      recurring: e.target.checked ? "Daily" : "None",
                    }))
                  }
                />
                <label
                  className="form-check-label small"
                  htmlFor="ptDaily"
                  title="Repeats every day"
                >
                  Daily
                </label>
              </div>
            </div>
            <div className="col-sm-1 d-grid">
              <button
                className="btn btn-primary btn-sm"
                disabled={saving}
                onClick={handleAdd}
              >
                + Add
              </button>
            </div>
            <div className="col-12">
              <input
                className="form-control form-control-sm"
                placeholder="Starting remark (optional)"
                value={draft.remarks}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, remarks: e.target.value }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2">
          <button
            className="btn btn-sm btn-outline-primary ms-auto order-last"
            onClick={() => navigate("/tasks/work-list")}
          >
            Work List →
          </button>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`btn btn-sm ${
                view === tab.key ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setView(tab.key)}
            >
              {tab.label}
              {tab.count !== undefined ? ` (${tab.count})` : ""}
            </button>
          ))}
        </div>

        <div className="card-header d-flex flex-wrap align-items-center gap-2 border-top">
          <span className="small text-muted me-1">Status:</span>
          <button
            className={`btn btn-sm ${
              statusFilter === "" ? "btn-dark" : "btn-outline-dark"
            }`}
            onClick={() => setStatusFilter("")}
          >
            All Status
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              className={`btn btn-sm ${
                statusFilter === s ? "btn-dark" : "btn-outline-dark"
              }`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
              {statusCount(s) !== undefined ? ` (${statusCount(s)})` : ""}
            </button>
          ))}
        </div>

        <div className="card-body p-0">
          {loading && (
            <div className="text-center text-muted py-4">Loading…</div>
          )}

          {!loading && tasks.length === 0 && (
            <div className="text-center text-muted py-4">
              {statusFilter
                ? `No ${statusFilter.toLowerCase()} tasks here.`
                : "No tasks here. Add one above."}
            </div>
          )}

          {!loading && tasks.length > 0 && (
            <ul className="list-group list-group-flush">
              {tasks.map((task, idx) => {
                // Date heading whenever the day changes (list is newest first).
                const key = dayKey(taskDate(task));
                const prevKey = idx > 0 ? dayKey(taskDate(tasks[idx - 1])) : null;
                const heading =
                  key !== prevKey ? (
                    <li
                      key={`day-${key}`}
                      className="list-group-item bg-light fw-semibold small text-uppercase text-muted py-2"
                    >
                      {fmtDayLabel(taskDate(task))}
                    </li>
                  ) : null;
                return (
                  <React.Fragment key={task._id}>
                    {heading}
                    {renderTask(task)}
                  </React.Fragment>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );

  function renderTask(task) {
    const isEditing = editingId === task._id;
    const dueLabel = fmtDue(task.dueAt);
    const overdue =
      !task.completed &&
      task.dueAt &&
      new Date(task.dueAt) < new Date().setHours(0, 0, 0, 0);

    if (isEditing) {
      return (
        <li key={task._id} className="list-group-item bg-light">
          <div className="row g-2 align-items-end">
            <div className="col-sm-4">
              <input
                className="form-control form-control-sm"
                value={editDraft.title}
                onChange={(e) =>
                  setEditDraft((p) => ({
                    ...p,
                    title: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-sm-2">
              <input
                type="date"
                className="form-control form-control-sm"
                value={editDraft.dueDate}
                onChange={(e) =>
                  setEditDraft((p) => ({
                    ...p,
                    dueDate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-sm-2">
              <input
                type="time"
                className="form-control form-control-sm"
                value={editDraft.dueTime}
                onChange={(e) =>
                  setEditDraft((p) => ({
                    ...p,
                    dueTime: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-sm-2">
              <select
                className="form-select form-select-sm"
                value={editDraft.priority}
                onChange={(e) =>
                  setEditDraft((p) => ({
                    ...p,
                    priority: e.target.value,
                  }))
                }
              >
                {PRIORITIES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="col-sm-2 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm flex-fill"
                disabled={saving}
                onClick={saveEdit}
              >
                Save
              </button>
              <button
                className="btn btn-outline-secondary btn-sm flex-fill"
                onClick={() => setEditingId(null)}
              >
                Cancel
              </button>
            </div>
            <div className="col-12">
              <input
                className="form-control form-control-sm"
                placeholder="Description (optional)"
                value={editDraft.description}
                onChange={(e) =>
                  setEditDraft((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </li>
      );
    }

    const statusDraft = getStatusDraft(task);
    const history = Array.isArray(task.statusHistory)
      ? task.statusHistory
      : [];
    const latestNote = [...history]
      .filter((h) => h.remarks)
      .slice(-1)[0];
    const historyOpen = openHistoryId === task._id;

    return (
      <li
        key={task._id}
        className="list-group-item"
        style={statusRowStyle(task.status)}
      >
        <div className="d-flex align-items-start gap-2">
          <input
            type="checkbox"
            className="form-check-input mt-1"
            checked={!!task.completed}
            onChange={() => handleToggle(task)}
            title="Quick complete / un-complete"
          />
          <div className="flex-grow-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span
                className={
                  task.completed
                    ? "text-decoration-line-through text-muted"
                    : "fw-semibold"
                }
              >
                {task.title}
              </span>
              <span className={statusBadge(task.status)}>
                {task.status}
              </span>
              <span className={priorityBadge(task.priority)}>
                {task.priority}
              </span>
              {task.recurring === "Daily" && (
                <span
                  className="badge bg-info text-dark"
                  title="Repeats every day"
                >
                  Daily
                </span>
              )}
              {dueLabel && (
                <span
                  className={`small ${overdue ? "text-danger fw-semibold" : "text-muted"}`}
                >
                  {overdue ? "Overdue · " : ""}
                  {dueLabel}
                </span>
              )}
            </div>
            {task.description && (
              <div className="small text-muted mt-1">
                {task.description}
              </div>
            )}
            {latestNote && (
              <div className="small text-muted mt-1">
                <span className="fw-semibold text-dark">
                  Latest remark ({latestNote.status}):
                </span>{" "}
                {latestNote.remarks}
              </div>
            )}

            {/* Stage update: status + remark for this transition */}
            <div className="row g-2 align-items-end mt-2">
              <div className="col-sm-3 col-6">
                <select
                  className="form-select form-select-sm"
                  value={statusDraft.status}
                  onChange={(e) =>
                    setStatusDraftField(
                      task,
                      "status",
                      e.target.value,
                    )
                  }
                >
                  {STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-sm-5 col-8">
                <input
                  className="form-control form-control-sm"
                  placeholder="Remark for this stage (optional)"
                  value={statusDraft.remarks}
                  onChange={(e) =>
                    setStatusDraftField(
                      task,
                      "remarks",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div className="col-sm-2 col-4 d-grid">
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={
                    saving ||
                    (statusDraft.status === task.status &&
                      !statusDraft.remarks.trim())
                  }
                  onClick={() => handleUpdateStatus(task)}
                >
                  Update
                </button>
              </div>
              {history.length > 0 && (
                <div className="col-sm-2 col-12">
                  <button
                    type="button"
                    className="btn btn-sm btn-link px-0"
                    onClick={() =>
                      setOpenHistoryId(
                        historyOpen ? null : task._id,
                      )
                    }
                  >
                    {historyOpen ? "Hide" : "View"} history (
                    {history.length})
                  </button>
                </div>
              )}
            </div>

            {historyOpen && (
              <div className="table-responsive mt-2">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history]
                      .reverse()
                      .map((entry, index) => (
                        <tr key={index}>
                          <td className="text-nowrap">
                            {fmtDateTime(entry.changedAt)}
                          </td>
                          <td>
                            <span
                              className={statusBadge(entry.status)}
                            >
                              {entry.status}
                            </span>
                          </td>
                          <td>{entry.remarks || "—"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => startEdit(task)}
            >
              Edit
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(task)}
            >
              Delete
            </button>
          </div>
        </div>
      </li>
    );
  }
};

export default MyTasks;
